import { type Dispatch, memo, type SetStateAction } from "react";

import { type ComparativeFeature } from "@doitintl/cmp-models";
import { Button, CircularProgress, Stack, Typography } from "@mui/material";

import { reportTxt } from "../../../../assets/texts/CloudAnalytics";
import { useReportConfig } from "../../Context";
import { type PopoverAnchorEl } from "../../report/types";
import { type ColKeySort } from "../../ReportData";
import { isTable } from "../../utilities";
import AnalyticsRendererContent from "./AnalyticsRendererContent";
import CountFieldMissing from "./CountFieldMissing";
import { ErrorAlerts } from "./ErrorAlerts";

type Props = {
  colKeySort?: ColKeySort | null;
  countFieldMissing: boolean;
  disableCacheBtn: boolean;
  forceRender: boolean;
  formatter: (value: number | string | undefined, short?: boolean, comparative?: ComparativeFeature) => string;
  isReportCachedBtnLoading: boolean;
  leftDrawerTransitioning: boolean;
  renderKey: number;
  reportError?: { code: string; message?: string };
  reportReady: boolean;
  reportQueryRunning: boolean;
  showForecast: boolean;
  setColKeySort: Dispatch<SetStateAction<ColKeySort | undefined>>;
  setPopoverAnchorEl: (anchorEl: PopoverAnchorEl | null) => void;
  onCacheReportClicked: () => void;
  onCancelReportRunClicked: () => void;
};

const PlotRenderer = ({
  onCacheReportClicked,
  onCancelReportRunClicked,
  colKeySort,
  countFieldMissing,
  disableCacheBtn,
  formatter,
  isReportCachedBtnLoading,
  leftDrawerTransitioning,
  renderKey,
  reportError,
  reportReady,
  reportQueryRunning,
  setColKeySort,
  setPopoverAnchorEl,
  showForecast,
}: Props) => {
  const {
    reportConfig: { renderer },
    reportData,
    reportDataWForecasts,
  } = useReportConfig();

  const isTableRenderer = isTable(renderer);
  if (leftDrawerTransitioning && !isTableRenderer) {
    return null;
  }

  if (countFieldMissing) {
    return <CountFieldMissing setPopoverAnchorEl={setPopoverAnchorEl} />;
  }

  if (reportQueryRunning) {
    return (
      <Stack
        sx={{
          alignItems: "center",
          pt: 20,
          pb: 5,
        }}
      >
        <CircularProgress size={34} />
        <Typography
          variant="subtitle2"
          sx={{
            mt: 1,
          }}
        >
          {reportTxt.LOADING}
        </Typography>
        <Button sx={{ mt: 6 }} onClick={onCancelReportRunClicked}>
          {reportTxt.STOP_GENERATING_REPORT}
        </Button>
      </Stack>
    );
  }

  if (reportError) {
    return (
      <ErrorAlerts
        onCacheReportClicked={onCacheReportClicked}
        disableCacheBtn={disableCacheBtn}
        errorCode={reportError.code}
        errorMessage={reportError?.message}
        isReportCachedBtnLoading={isReportCachedBtnLoading}
      />
    );
  } else if (reportReady) {
    const forecastStart = reportData?.current?.getForecastStartIndex() ?? 0;
    const isForecast = showForecast && !!reportDataWForecasts;
    const dataProp = isForecast && reportDataWForecasts.current ? reportDataWForecasts.current : reportData.current;
    if (!dataProp) {
      return null;
    }
    return (
      <AnalyticsRendererContent
        colKeySort={colKeySort}
        dataProp={dataProp}
        forecastStart={forecastStart}
        formatter={formatter}
        renderKey={renderKey}
        setColKeySort={setColKeySort}
      />
    );
  }

  return null;
};

export default memo(PlotRenderer);
